import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Plus, Loader2, Copy, Pencil, Trash2, Lock, FileText,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  getMarketUpdateTemplates, deleteMarketUpdateTemplate,
  duplicateMarketUpdateTemplate,
} from "../api/prospectingReportsApi";
import TemplateEditorModal from "../components/TemplateEditorModal";

const TEAL = "#55c5d0";

const SCOPE_TABS = [
  { key: "all", label: "All templates" },
  { key: "agency", label: "Agency templates" },
  { key: "system", label: "System templates" },
];

/**
 * Manage your templates — the page behind the "Manage templates" button in the
 * ticket's screenshot. Agency templates are editable; system templates are
 * read-only and can only be duplicated, which is what Ignite does too.
 */
const MarketUpdateTemplatesPage = () => {
  const navigate = useNavigate();
  // Defaults to "all" so the built-in system templates are visible on a fresh
  // install. Landing on "agency" showed an empty grid even though three
  // templates shipped, which reads as "this feature is broken".
  const [scope, setScope] = useState("all");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template object, or {} for new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getMarketUpdateTemplates({ scope });
      setTemplates(data?.results || []);
    } catch (error) {
      toast.error("Could not load templates.");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const handleDuplicate = async (template) => {
    try {
      const { data } = await duplicateMarketUpdateTemplate(template.id);
      toast.success("Copied to your agency templates.");
      setScope("agency");
      setEditing(data);
    } catch (error) {
      toast.error("Could not duplicate the template.");
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    try {
      await deleteMarketUpdateTemplate(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
      toast.success("Template deleted.");
    } catch (error) {
      toast.error("Could not delete the template.");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <button
        type="button"
        onClick={() => navigate("/prospecting/market-update")}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Market Update
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage your templates</h1>
          <p className="text-sm text-gray-500 mt-1">
            Build the emails you send to your client list. Merge tags fill in each suburb's numbers
            when the update is created.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold"
          style={{ backgroundColor: TEAL }}
        >
          <Plus className="w-4 h-4" />
          New template
        </button>
      </div>

      <div className="flex items-center gap-2 mb-5">
        {SCOPE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setScope(tab.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              scope === tab.key
                ? "border-gray-900 text-gray-900"
                : "border-gray-200 text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-xl py-16 text-center">
          <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 mb-3">
            {scope === "system"
              ? "No system templates are installed yet."
              : "No templates here yet."}
          </p>
          {scope !== "system" && (
            <button
              type="button"
              onClick={() => setEditing({})}
              className="text-sm font-semibold underline text-gray-700 hover:text-gray-900"
            >
              Create your first template
            </button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="rounded-xl border border-gray-200 overflow-hidden bg-white flex flex-col"
            >
              <div className="h-32 bg-gray-100 relative overflow-hidden">
                {template.cover_image_url ? (
                  <img src={template.cover_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ backgroundColor: "var(--c-panel-brand)" }}
                  >
                    <FileText className="w-8 h-8" style={{ color: TEAL }} />
                  </div>
                )}
                {!template.is_editable && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 text-[10px] font-bold text-gray-600">
                    <Lock className="w-3 h-3" /> System
                  </span>
                )}
              </div>

              <div className="p-4 flex-1">
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-[#e8f7f9] text-[#2a8f99] mb-2">
                  Market Update
                </span>
                <p className="text-sm font-bold text-gray-900">{template.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {template.office_label || "Collings Real Estate"}
                </p>
                {template.subject && (
                  <p className="text-xs text-gray-400 mt-2 truncate">Subject: {template.subject}</p>
                )}
              </div>

              <div className="flex items-center gap-1 px-3 py-2 border-t border-gray-100">
                {template.is_editable ? (
                  <button
                    type="button"
                    onClick={() => setEditing(template)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditing({ ...template, readOnly: true })}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
                  >
                    <FileText className="w-3.5 h-3.5" /> View
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDuplicate(template)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
                >
                  <Copy className="w-3.5 h-3.5" /> Duplicate
                </button>
                {template.is_editable && (
                  <button
                    type="button"
                    onClick={() => handleDelete(template)}
                    className="ml-auto p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600"
                    aria-label="Delete template"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditorModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export default MarketUpdateTemplatesPage;
